/**
 * E-Store BackOffice Application
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute, ErrorBoundary } from '@e-store/shared';
import { LoginPage } from './pages/LoginPage';
import { DashboardPageNew as DashboardPage } from './pages/DashboardPageNew';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import './styles/buttons.css';
import './styles/responsive.css';
import './styles/page-header.css';

// Lazy-loaded pages (chargees a la demande)
const EnterpriseConfigPage = lazy(() => import('./pages/EnterpriseConfigPage').then(m => ({ default: m.EnterpriseConfigPage })));
const SitesPage = lazy(() => import('./pages/SitesPage').then(m => ({ default: m.SitesPage })));
const ArticlesPage = lazy(() => import('./pages/ArticlesPage').then(m => ({ default: m.ArticlesPage })));
const ArticleCategoriesPage = lazy(() => import('./pages/ArticleCategoriesPage').then(m => ({ default: m.ArticleCategoriesPage })));
const StocksPage = lazy(() => import('./pages/StocksPage').then(m => ({ default: m.StocksPage })));
const StockHistoryPage = lazy(() => import('./pages/StockHistoryPage'));
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage })));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage').then(m => ({ default: m.InvoicesPage })));
const ContactsPage = lazy(() => import('./pages/ContactsPage').then(m => ({ default: m.ContactsPage })));
const SalariesPage = lazy(() => import('./pages/SalariesPage').then(m => ({ default: m.SalariesPage })));
const SalaryDeductionsPage = lazy(() => import('./pages/SalaryDeductionsPage').then(m => ({ default: m.SalaryDeductionsPage })));
const OrdersPage = lazy(() => import('./pages/OrdersPage').then(m => ({ default: m.OrdersPage })));
const InventoryPage = lazy(() => import('./pages/InventoryPage').then(m => ({ default: m.InventoryPage })));
const AccountingPage = lazy(() => import('./pages/AccountingPage').then(m => ({ default: m.AccountingPage })));
const CashRegisterPage = lazy(() => import('./pages/CashRegisterPage').then(m => ({ default: m.CashRegisterPage })));
const SalesReportPage = lazy(() => import('./pages/SalesReportPage').then(m => ({ default: m.SalesReportPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const FAQPage = lazy(() => import('./pages/FAQPage').then(m => ({ default: m.FAQPage })));
const FAQManagementPage = lazy(() => import('./pages/FAQManagementPage').then(m => ({ default: m.FAQManagementPage })));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage').then(m => ({ default: m.ReviewsPage })));
const StockArrivalsPage = lazy(() => import('./pages/StockArrivalsPage').then(m => ({ default: m.StockArrivalsPage })));
const EmployeeDelaysPage = lazy(() => import('./pages/EmployeeDelaysPage').then(m => ({ default: m.EmployeeDelaysPage })));
const EmployeeSelfServicePage = lazy(() => import('./pages/EmployeeSelfServicePage').then(m => ({ default: m.EmployeeSelfServicePage })));
const PayrollPage = lazy(() => import('./pages/PayrollPage').then(m => ({ default: m.PayrollPage })));
const HistoryPage = lazy(() => import('./pages/HistoryPage').then(m => ({ default: m.HistoryPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const SuperAdminEnterprisesPage = lazy(() => import('./pages/SuperAdminEnterprisesPage').then(m => ({ default: m.SuperAdminEnterprisesPage })));
const SuperAdminModulesPage = lazy(() => import('./pages/SuperAdminModulesPage').then(m => ({ default: m.SuperAdminModulesPage })));
const AccountingReportsPage = lazy(() => import('./pages/AccountingReportsPage').then(m => ({ default: m.AccountingReportsPage })));

function LazyFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <p>Chargement...</p>
    </div>
  );
}

function Protected({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute portal="backoffice" loginPath="/login" changePasswordPath="/change-password">
      {children}
    </ProtectedRoute>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LazyFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/change-password" element={<Protected><ChangePasswordPage /></Protected>} />
          <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
          <Route path="/config/enterprise" element={<Protected><EnterpriseConfigPage /></Protected>} />
          <Route path="/config/sites" element={<Protected><SitesPage /></Protected>} />
          <Route path="/articles" element={<Protected><ArticlesPage /></Protected>} />
          <Route path="/articles/categories" element={<Protected><ArticleCategoriesPage /></Protected>} />
          <Route path="/stocks" element={<Protected><StocksPage /></Protected>} />
          <Route path="/stocks/history" element={<Protected><StockHistoryPage /></Protected>} />
          <Route path="/users" element={<Protected><UsersPage /></Protected>} />
          <Route path="/invoices" element={<Protected><InvoicesPage /></Protected>} />
          <Route path="/contacts" element={<Protected><ContactsPage /></Protected>} />
          <Route path="/salaries" element={<Protected><SalariesPage /></Protected>} />
          <Route path="/orders" element={<Protected><OrdersPage /></Protected>} />
          <Route path="/inventory" element={<Protected><InventoryPage /></Protected>} />
          <Route path="/accounting" element={<Protected><AccountingPage /></Protected>} />
          <Route path="/accounting-reports" element={<Protected><AccountingReportsPage /></Protected>} />
          <Route path="/cash-register" element={<Protected><CashRegisterPage /></Protected>} />
          <Route path="/sales-reports" element={<Protected><SalesReportPage /></Protected>} />
          <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
          <Route path="/faq" element={<Protected><FAQPage /></Protected>} />
          <Route path="/faq/management" element={<Protected><FAQManagementPage /></Protected>} />
          <Route path="/reviews" element={<Protected><ReviewsPage /></Protected>} />
          <Route path="/stock-arrivals" element={<Protected><StockArrivalsPage /></Protected>} />
          <Route path="/employee-delays" element={<Protected><EmployeeDelaysPage /></Protected>} />
          <Route path="/my-salary" element={<Protected><EmployeeSelfServicePage /></Protected>} />
          <Route path="/payroll" element={<Protected><PayrollPage /></Protected>} />
          <Route path="/salary-deductions" element={<Protected><SalaryDeductionsPage /></Protected>} />
          <Route path="/history" element={<Protected><HistoryPage /></Protected>} />
          <Route path="/analytics" element={<Protected><AnalyticsPage /></Protected>} />
          {/* Routes Super Admin */}
          <Route path="/super-admin/enterprises" element={<Protected><SuperAdminEnterprisesPage /></Protected>} />
          <Route path="/super-admin/enterprises/:enterpriseId" element={<Protected><SuperAdminModulesPage /></Protected>} />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
